// Timing, colour and particle data for the badge reveals. Pure, so the pacing can
// be tested without a DOM.
import { BADGE_REVEAL_MS, BADGE_REVEAL_MS_REDUCED, tierStyle } from "../art";

// ---------- timing ----------

/**
 * The cinematic after the member taps the sealed badge, in ms from the tap.
 *   charge  the silhouette strains and the light gathers (anticipation)
 *   burst   flash, rings, particles; the badge's own layer reveal starts
 *   rarity  the tier line, after the art has settled and a beat of silence
 *   name    the name lands, after a pause of its own
 *   stamp   first on the site only: the stamp slams in after the name, then the
 *           gold burst and the ring of crowns (STAMP_IMPACT_MS after it)
 *   details description and the repeat count
 *   actions Collect, Share, See collection
 */
export interface CinematicTimeline {
  burst: number;
  rarity: number;
  name: number;
  /** null unless the copy was the first on the site. */
  stamp: number | null;
  details: number;
  actions: number;
}

/** From the stamp's start to its impact, when the gold burst fires. */
export const STAMP_IMPACT_MS = 280;

export function cinematicTimeline(reduced: boolean, first = false): CinematicTimeline {
  if (reduced) {
    // No charge, no burst: the art fades in, then the words, still in order.
    const settled = BADGE_REVEAL_MS_REDUCED;
    const name = settled + 350;
    if (first) {
      const stamp = name + 250;
      return { burst: 0, rarity: settled + 50, name, stamp, details: stamp + 200, actions: stamp + 350 };
    }
    return { burst: 0, rarity: settled + 50, name, stamp: null, details: settled + 500, actions: settled + 650 };
  }
  const burst = 560;
  const settled = burst + BADGE_REVEAL_MS; // the counter chip has landed
  const rarity = settled + 380;
  const name = rarity + 950; // the pause before the name
  if (first) {
    // the name lands, a held breath, the stamp; the gold light has its moment
    // before anything else moves
    const stamp = name + 850;
    return { burst, rarity, name, stamp, details: stamp + 1000, actions: stamp + 1500 };
  }
  return { burst, rarity, name, stamp: null, details: name + 520, actions: name + 1000 };
}

/** The card: it pops in, then the art reveals, then the words. Ms from mount. */
export interface CardTimeline {
  art: number;
  burst: number;
  name: number;
  /** null unless the copy was the first on the site. */
  stamp: number | null;
  details: number;
  actions: number;
}

export function cardTimeline(reduced: boolean, first = false): CardTimeline {
  if (reduced) return { art: 0, burst: 0, name: 250, stamp: first ? 300 : null, details: 300, actions: 400 };
  const art = 200;
  if (first) {
    const stamp = art + 1500;
    return { art, burst: art + 520, name: art + 950, stamp, details: stamp + 250, actions: stamp + 550 };
  }
  return { art, burst: art + 520, name: art + 950, stamp: null, details: art + 1200, actions: art + 1450 };
}

// ---------- colour ----------

/** The second burst of a first-ever cinematic, and its crowns: the Gold tier's colours. */
export const FIRST_GOLD = { fill: "#f4bd4d", gloss: "#ffe6bd", deep: "#df9110" } as const;

/** "#a855f7" + 0.5 -> "rgba(168, 85, 247, 0.5)" */
export function hexAlpha(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) return `rgba(255, 255, 255, ${alpha})`;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
}

/**
 * The colour of the stage light for a tier: a gem's glow, a coin's metal, rock's
 * pale grey, paper's warm white.
 */
export function stageLight(tier: string): string {
  const t = tierStyle(tier);
  if (t.glow) return t.glow;
  if (t.kind === "rock") return t.gloss;
  return t.fill;
}

// ---------- particles ----------

export type ParticleShape = "chip" | "spark" | "strip";

export interface Particle {
  shape: ParticleShape;
  /** Final offset from the centre, px. */
  dx: number;
  dy: number;
  /** Extra downward drift at the end, px. */
  fall: number;
  size: number;
  rotate: number;
  delay: number;
  duration: number;
  color: string;
}

function hashSeed(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(a: number): () => number {
  let s = a;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Burst particles, seeded so a copy always bursts the same way (and tests are
 * stable). `spread` is the typical travel distance in px.
 */
export function makeParticles(seed: string, count: number, spread: number, colors: string[]): Particle[] {
  const rand = mulberry32(hashSeed(seed));
  const palette = colors.length ? colors : ["#ffffff"];
  const out: Particle[] = [];
  for (let i = 0; i < count; i++) {
    // spread the angles evenly with jitter, so no side of the badge is left bare
    const angle = ((i + rand() * 0.8) / count) * Math.PI * 2;
    const dist = spread * (0.55 + rand() * 0.75);
    const roll = rand();
    const shape: ParticleShape = roll < 0.5 ? "chip" : roll < 0.8 ? "spark" : "strip";
    out.push({
      shape,
      dx: Math.round(Math.cos(angle) * dist),
      dy: Math.round(Math.sin(angle) * dist),
      fall: Math.round(spread * (0.12 + rand() * 0.3)),
      size: Math.round(shape === "spark" ? 4 + rand() * 5 : 6 + rand() * 8),
      rotate: Math.round((rand() - 0.5) * 900),
      delay: Math.round(rand() * 140),
      duration: Math.round(1000 + rand() * 700),
      color: palette[Math.floor(rand() * palette.length) % palette.length],
    });
  }
  return out;
}

/** How many particles a burst gets, by where it plays and the badge's tier. */
export function particleCount(where: "cinematic" | "card", tier: string, firstEver = false): number {
  if (where === "card") return firstEver ? 20 : 14;
  const t = String(tier).toUpperCase();
  return t === "DIAMOND" ? 46 : t === "RUBY" || t === "EMERALD" ? 34 : 26;
}
